import { supabaseClient } from '../../services/supabase';
import {
  fetchCaliforniaStateEnergyActivityFromGhgInventory,
  isCaliforniaStateFromPopulationRow
} from '../../services/californiaGhgInventoryEnergyActivityService';
import {
  energyFormatGeographyTypes,
  geofipsVariantsForEnergyFormat,
  stateGeofipsVariantsForEnergyFormat
} from '../../utils/energyFormatQuery';

export interface EnergySeriesResult {
  chartData: any;
  chartOptions: any;
  latestYear?: number;
  latestValue?: number;
  availableCommercialModels?: CommercialExtrapolationModel[];
  commercialObservedInterpolatedYears?: number;
  commercialDepthBucket?: CommercialDepthBucket;
  /** True when all data is observed (no extrapolation). Show "100% observed data" instead of model selector. */
  allDataObserved?: boolean;
}

type Sector = 'residential' | 'commercial';

export type CommercialExtrapolationModel =
  | 'average'
  | 'm1'
  | 'm2'
  | 'm3'
  | 'm4'
  | 'm5'
  | 'm6'
  | 'm7';

export type CommercialDepthBucket = '1-4' | '5-9' | '10+';

export const COMMERCIAL_MODEL_OPTIONS: Array<{ value: CommercialExtrapolationModel; label: string }> = [
  { value: 'average', label: 'Average (All Models)' },
  { value: 'm1', label: 'M1 Worker Intensity' },
  { value: 'm2', label: 'M2 Log-Linear Trend' },
  { value: 'm3', label: 'M3 County Growth Propagation' },
  { value: 'm4', label: 'M4 Ratio-to-County' },
  { value: 'm5', label: 'M5 Panel FE (City + Year)' },
  { value: 'm6', label: 'M6 City FE + Polynomial Time' },
  { value: 'm7', label: 'M7 Statewide Elasticity Scaling' }
];

const MODEL_LABELS: Record<CommercialExtrapolationModel, string> = {
  average: 'Average (All Models)',
  m1: 'M1 Worker Intensity',
  m2: 'M2 Log-Linear Trend',
  m3: 'M3 County Growth Propagation',
  m4: 'M4 Ratio-to-County',
  m5: 'M5 Panel FE (City + Year)',
  m6: 'M6 City FE + Polynomial Time',
  m7: 'M7 Statewide Elasticity Scaling'
};

const MODEL_SMAPE_BY_BUCKET: Record<'electricity' | 'naturalGas', Record<CommercialDepthBucket, Record<CommercialExtrapolationModel, number>>> = {
  electricity: {
    '1-4': { average: 0.0730208848017103, m1: 0.08782250860088826, m2: 0.07062576466415772, m3: 0.07720415092910078, m4: 0.07707941526314942, m5: 0.0664033743837006, m6: 0.05874972763836698, m7: 0.07326125213260835 },
    '5-9': { average: 0.06517051566972833, m1: 0.08750522668445872, m2: 0.043756918442160564, m3: 0.08788733601775056, m4: 0.045979857043483785, m5: 0.043892006665215984, m6: 0.05423533422621408, m7: 0.0929369306088146 },
    '10+': { average: 0.1169525964542575, m1: 0.2124297121307452, m2: 0.06026558728542412, m3: 0.11842791111971002, m4: 0.11017312257679153, m5: 0.084225683157603, m6: 0.07578954723585224, m7: 0.15735661167367632 }
  },
  naturalGas: {
    '1-4': { average: 0.18303112242005196, m1: 0.20639009792856614, m2: 0.2405219639621841, m3: 0.17360082938984367, m4: 0.160882915727798, m5: 0.18445149737704267, m6: 0.15697482410121133, m7: 0.15839572845371772 },
    '5-9': { average: 0.1330766231145452, m1: 0.16542893574333023, m2: 0.1461656419067583, m3: 0.13698153389954335, m4: 0.13668733865123037, m5: 0.09847812028898385, m6: 0.1106185522324798, m7: 0.1371762390794906 },
    '10+': { average: 0.11260692805390402, m1: 0.1573705628948421, m2: 0.10412971520141907, m3: 0.10306996377098206, m4: 0.09397749889547048, m5: 0.0953577235860183, m6: 0.09856958875427634, m7: 0.1357734432743197 }
  }
};

export function getCommercialDepthBucket(observedPlusInterpolatedYears?: number): CommercialDepthBucket {
  const n = Number(observedPlusInterpolatedYears || 0);
  if (n >= 10) return '10+';
  if (n >= 5) return '5-9';
  return '1-4';
}

export function formatCommercialModelLabelWithSmape(
  model: CommercialExtrapolationModel,
  fuelType: 'electricity' | 'naturalGas',
  observedPlusInterpolatedYears?: number
): string {
  const base = MODEL_LABELS[model] || model;
  if (!Number.isFinite(Number(observedPlusInterpolatedYears))) return base;
  const bucket = getCommercialDepthBucket(observedPlusInterpolatedYears);
  const smape = MODEL_SMAPE_BY_BUCKET[fuelType]?.[bucket]?.[model];
  if (!Number.isFinite(smape)) return base;
  return `${base} (avg error ${((smape as number) * 100).toFixed(1)}%)`;
}

// Sector-specific color shades (dark=observed, medium=interpolated, light=extrapolated)
const sectorColors: Record<Sector, { observed: string; interpolated: string; extrapolated: string }> = {
  residential: {
    observed: '#1e40af',      // Dark blue
    interpolated: '#3b82f6',  // Medium blue
    extrapolated: '#8b5cf6'   // Purple (clear contrast vs interpolated)
  },
  commercial: {
    observed: '#166534',      // Dark green
    interpolated: '#22c55e',  // Medium green
    extrapolated: '#f59e0b'   // Amber (clear contrast vs interpolated)
  }
};

const commonOptions = (title: string, yLabel: string, stacked = false) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { 
      position: 'bottom',
      labels: {
        padding: 10,
        usePointStyle: true,
        pointStyle: 'rect'
      }
    },
    title: { display: true, text: title },
    tooltip: {
      callbacks: {
        label: (ctx: any) => {
          const v = Number(ctx.parsed.y);
          return `${ctx.dataset.label}: ${Number.isFinite(v) ? v.toLocaleString() : v}`;
        }
      }
    }
  },
  scales: {
    x: { stacked, title: { display: true, text: 'Year' } },
    y: { stacked, beginAtZero: true, title: { display: true, text: yLabel }, ticks: { callback: (v: any) => Number(v).toLocaleString() } }
  },
  layout: {
    padding: {
      bottom: 20
    }
  }
});

type PanelRow = {
  geofips: string;
  year: number;
  total_kwh: number;
  total_worker: number;
  measurement: string;
  method_note: string;
};

type LocalModelRow = {
  year: number;
  total_kwh: number;
  total_worker: number;
  county_total_kwh: number;
  extrapolated_new: number;
  city_obs_interp_years: number;
  models_included_in_average: string;
  pred_m1: number;
  pred_m2: number;
  pred_m3: number;
  pred_m4: number;
  pred_m5: number;
  pred_m6: number;
  pred_m7: number;
  guardrail_m1: string;
  guardrail_m2: string;
  guardrail_m3: string;
  guardrail_m4: string;
  guardrail_m5: string;
  guardrail_m6: string;
  guardrail_m7: string;
  measurement: string;
  method_note: string;
  extrapolation_model: string;
};

/** Pipeline uses `measurement: observed` for some modeled / CEC county rows; use keywords to exclude from strict "Observed" bars (NREL is handled separately — charts show it as Observed). */
function isSyntheticUtilityAdjacentMethodNote(n: string): boolean {
  if (!n) return false;
  return (
    n.includes('cbecs') ||
    n.includes('modeled') ||
    n.includes('modelled') ||
    n.includes('synthetic') ||
    n.includes('imput') ||
    n.includes('backcast')
  );
}

/** Cities and counties use the same format-table tags; interpolation may be in `measurement` and/or `method_note`. */
function isObservedEnergyRow(measurement: string, methodNote: string): boolean {
  const m = String(measurement || '').toLowerCase().trim();
  const n = String(methodNote || '').toLowerCase().trim();
  if (m !== 'observed') return false;
  if (n.includes('extrapol')) return false;
  if (n === 'na' || n === 'n/a') return false;
  // NREL City Profiles (e.g. `modeled by NREL`): pipeline uses measurement=observed — show as Observed (green) in charts.
  if (n.includes('nrel')) return true;
  if (isSyntheticUtilityAdjacentMethodNote(n)) return false;
  return true;
}

function isInterpolatedEnergyRow(measurement: string, methodNote: string): boolean {
  const m = String(measurement || '').toLowerCase().trim();
  const n = String(methodNote || '').toLowerCase().trim();
  if (isObservedEnergyRow(measurement, methodNote)) return false;
  if (n === 'na' || n === 'n/a') return true;
  if (m.includes('interpol') || n.includes('interpol')) return true;
  if (m === 'estimated' && !n.includes('extrapol')) return true;
  return false;
}

/**
 * Bar coloring: prefer `extrapolation_model` when present (commercial model stack), then measurement/method_note.
 * County/state CEC totals use `method_note` "NA" and must not show as utility-observed.
 */
export function classifyEnergyRowProvenance(
  measurement: string | undefined | null,
  methodNote: string | undefined | null,
  extrapolationModel?: string | null
): 'observed' | 'interpolated' | 'extrapolated' {
  const n = String(methodNote || '').toLowerCase().trim();
  if (n === 'na' || n === 'n/a') return 'interpolated';

  const em = String(extrapolationModel || '').trim().toUpperCase();
  if (em === 'INTERPOLATED') return 'interpolated';
  if (em && em !== 'OBSERVED') return 'extrapolated';

  const mStr = String(measurement || '');
  if (isObservedEnergyRow(mStr, n)) return 'observed';
  if (isInterpolatedEnergyRow(mStr, n)) return 'interpolated';
  return 'extrapolated';
}

const provenancePriority = (measurement: string, methodNote: string, extrapolationModel?: string | null) => {
  const c = classifyEnergyRowProvenance(measurement, methodNote, extrapolationModel);
  return c === 'observed' ? 3 : c === 'interpolated' ? 2 : 1;
};

const isTrainingRow = (measurement: string, methodNote: string) =>
  isObservedEnergyRow(measurement, methodNote) || isInterpolatedEnergyRow(measurement, methodNote);

const asNum = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

/** Sum Observed + Interpolated + Extrapolated bar segments per year (Energy page stacked totals). */
function totalByYearFromEnergyChartData(chartData: { labels?: number[]; datasets?: any[] }): Map<number, number> {
  const labels = (chartData.labels || []) as number[];
  const datasets = chartData.datasets || [];
  const map = new Map<number, number>();
  labels.forEach((y, i) => {
    let t = 0;
    datasets.forEach((ds: any) => {
      t += asNum(ds.data?.[i]) || 0;
    });
    map.set(y, t);
  });
  return map;
}

const mean = (arr: number[]): number => {
  if (!arr.length) return NaN;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
};

const percentile = (arr: number[], p: number): number => {
  if (!arr.length) return NaN;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  const t = idx - lo;
  return sorted[lo] * (1 - t) + sorted[hi] * t;
};

const hasGuardrailPass = (row: LocalModelRow | undefined, model: Exclude<CommercialExtrapolationModel, 'average'>): boolean => {
  if (!row) return false;

  const guardrailValue = String((row as any)[`guardrail_${model}`] || '').toUpperCase().trim();
  if (guardrailValue === 'PASS') return true;
  if (guardrailValue === 'FAIL') return false;

  // Fallback 1: use precomputed average-membership list when guardrail fields are missing.
  const included = String(row.models_included_in_average || '').toUpperCase();
  if (included) {
    const modelTag = model.toUpperCase();
    const includedSet = new Set(included.split('|').map((s) => s.trim()).filter(Boolean));
    if (includedSet.has(modelTag)) return true;
  }

  // Fallback 2: if no pass/fail metadata is present, allow any finite prediction.
  const pred = getDbModelPrediction(row, model);
  return Number.isFinite(pred) && pred > 0;
};

const getDbModelPrediction = (row: LocalModelRow | undefined, model: Exclude<CommercialExtrapolationModel, 'average'>): number => {
  if (!row) return NaN;
  return asNum((row as any)[`pred_${model}`]);
};

/** True when the raw Supabase row carries model outputs even if total_kwh is 0 (pipeline placeholders). */
function commercialEnergyRowHasModelSignal(row: any): boolean {
  if (!row) return false;
  if (Number.isFinite(asNum(row.extrapolated_new)) && asNum(row.extrapolated_new) > 0) return true;
  for (const m of ['m1', 'm2', 'm3', 'm4', 'm5', 'm6', 'm7'] as const) {
    const p = asNum(row[`pred_${m}`]);
    if (Number.isFinite(p) && p > 0) return true;
  }
  return false;
}

/**
 * Interior calendar years with no DB row: linear interpolation between observed/interpolated anchors
 * (same pattern as GHG populate `processEnergyData` gap fill). Mutates `interpolatedMap`.
 */
function fillInteriorLinearGapsEnergy(
  years: number[],
  observedMap: Map<number, number>,
  interpolatedMap: Map<number, number>,
  extrapolatedMap: Map<number, number>
): void {
  const anchorYears = Array.from(observedMap.keys())
    .concat(Array.from(interpolatedMap.keys()))
    .filter((y, i, a) => a.indexOf(y) === i)
    .sort((a, b) => a - b);
  if (anchorYears.length < 2) return;
  const minA = anchorYears[0];
  const maxA = anchorYears[anchorYears.length - 1];
  const anchorVal = (y: number): number =>
    observedMap.has(y) ? (observedMap.get(y) as number) : (interpolatedMap.get(y) as number);

  for (const y of years) {
    if (observedMap.has(y) || interpolatedMap.has(y) || extrapolatedMap.has(y)) continue;
    if (y <= minA || y >= maxA) continue;
    let y0 = -1;
    let y1 = -1;
    for (let i = 0; i < anchorYears.length - 1; i++) {
      if (anchorYears[i] < y && y < anchorYears[i + 1]) {
        y0 = anchorYears[i];
        y1 = anchorYears[i + 1];
        break;
      }
    }
    if (y0 < 0) continue;
    const v0 = anchorVal(y0);
    const v1 = anchorVal(y1);
    if (!Number.isFinite(v0) || !Number.isFinite(v1)) continue;
    const t = (y - y0) / (y1 - y0);
    interpolatedMap.set(y, v0 + t * (v1 - v0));
  }
}

/** True if year lies strictly between earliest and latest observed/interpolated training years. */
function isStrictlyInteriorTrainingGap(
  year: number,
  observedMap: Map<number, number>,
  interpolatedMap: Map<number, number>
): boolean {
  const anchorYears = Array.from(observedMap.keys())
    .concat(Array.from(interpolatedMap.keys()))
    .filter((y, i, a) => a.indexOf(y) === i)
    .sort((a, b) => a - b);
  if (anchorYears.length < 2) return false;
  return year > anchorYears[0] && year < anchorYears[anchorYears.length - 1];
}

/** LODES workplace counts for chart years (aligns with Energy.tsx). */
async function fetchLodesWorkerMap(
  rawGeofips: string,
  popGeographyType: string | null | undefined,
  years: number[]
): Promise<Map<number, number>> {
  const out = new Map<number, number>();
  const gtLower = String(popGeographyType || '')
    .trim()
    .toLowerCase();
  const digits = String(rawGeofips || '').replace(/\D/g, '') || '';
  const workerGeofips =
    gtLower === 'state' ? '6' : gtLower === 'county' ? digits.slice(-4) : digits.slice(-6);
  const lodesGeo = gtLower === 'state' ? 'state' : gtLower === 'county' ? 'county' : 'place';

  const ingest = (rows: any[] | null | undefined) => {
    (rows || []).forEach((row: any) => {
      const y = asNum(row.year);
      const w = asNum(row.total_worker);
      if (Number.isFinite(y) && Number.isFinite(w) && w > 0) out.set(y, w);
    });
  };

  const run = async (gf: string) => {
    const res = await supabaseClient
      .from('lodes_employees_workplace')
      .select('year, total_worker')
      .eq('geofips', gf)
      .eq('geography_type', lodesGeo)
      .in('year', years)
      .order('year', { ascending: true });
    if (!res.error && res.data) ingest(res.data);
  };

  await run(workerGeofips || digits.slice(-6));
  if (out.size === 0 && gtLower !== 'state' && gtLower !== 'county') {
    const alt = digits.slice(-6);
    if (alt && alt !== (workerGeofips || '')) await run(alt);
  }
  return out;
}

/** Prefer per-year energy-table denom; fill from LODES; linear interpolate remaining gaps (see ENERGY_CALCULATION_METHODS.md). */
function mergeWorkerMapsWithLinearFill(
  years: number[],
  fromEnergy: Map<number, number>,
  fromLodes: Map<number, number>
): Map<number, number> {
  const m = new Map<number, number>();
  years.forEach((y) => {
    const ev = fromEnergy.get(y);
    if (Number.isFinite(ev) && (ev as number) > 0) m.set(y, ev as number);
    else {
      const lv = fromLodes.get(y);
      if (Number.isFinite(lv) && (lv as number) > 0) m.set(y, lv as number);
    }
  });
  const ys = [...years].sort((a, b) => a - b);
  for (const y of ys) {
    if (m.has(y) && (m.get(y) as number) > 0) continue;
    let lo = -Infinity;
    let hi = Infinity;
    let vlo = 0;
    let vhi = 0;
    for (const k of ys) {
      const v = m.get(k);
      if (!Number.isFinite(v) || (v as number) <= 0) continue;
      if (k < y && k > lo) {
        lo = k;
        vlo = v as number;
      }
      if (k > y && k < hi) {
        hi = k;
        vhi = v as number;
      }
    }
    if (lo !== -Infinity && hi !== Infinity) {
      m.set(y, vlo + ((vhi - vlo) * (y - lo)) / (hi - lo));
    } else if (lo !== -Infinity) m.set(y, vlo);
    else if (hi !== Infinity) m.set(y, vhi);
  }
  return m;
}

function buildStateElasticityByYear(
  years: number[],
  stateEnergyData: any[] | null | undefined,
  denomKey: 'total_worker' | 'population'
): {
  stateValueMap: Map<number, number>;
  stateDenomMap: Map<number, number>;
  stateElasticityByYear: Map<number, number>;
} {
  const stateValueMap = new Map<number, number>();
  const stateDenomMap = new Map<number, number>();
  (stateEnergyData || []).forEach((row: any) => {
    const year = asNum(row.year);
    const value = asNum(row.total_kwh);
    const denomValue = asNum(row[denomKey]);
    const measurement = String(row.measurement || '').toLowerCase().trim();
    const methodNote = String(row.method_note || '').toLowerCase().trim();
    const isObserved = measurement === 'observed' && !methodNote.includes('extrapol');
    if (Number.isFinite(year) && Number.isFinite(value) && value > 0) {
      if (isObserved || !stateValueMap.has(year)) stateValueMap.set(year, value);
    }
    if (Number.isFinite(year) && Number.isFinite(denomValue) && denomValue > 0) {
      if (!stateDenomMap.has(year)) stateDenomMap.set(year, denomValue);
    }
  });
  const stateElasticityByYear = new Map<number, number>();
  years.forEach((year) => {
    const prevYear = year - 1;
    const k0 = stateValueMap.get(prevYear);
    const k1 = stateValueMap.get(year);
    const d0 = stateDenomMap.get(prevYear);
    const d1 = stateDenomMap.get(year);
    if (!k0 || !k1 || !d0 || !d1) return;
    if (k0 <= 0 || k1 <= 0 || d0 <= 0 || d1 <= 0) return;
    const logK = Math.log(k1 / k0);
    const logD = Math.log(d1 / d0);
    if (!Number.isFinite(logK) || !Number.isFinite(logD) || logD === 0) return;
    stateElasticityByYear.set(year, logK / logD);
  });
  return { stateValueMap, stateDenomMap, stateElasticityByYear };
}

async function fetchCountyCommercialEnergySeries(
  table: 'elec_com_format' | 'ng_com_format',
  valueColumn: 'total_kwh' | 'total_therms',
  countyField: string | null | undefined,
  years: number[]
): Promise<Map<number, number>> {
  const countySeries = new Map<number, number>();
  if (!countyField) return countySeries;
  const countyName = String(countyField).replace(/\s*County$/i, '').trim();
  if (!countyName) return countySeries;
  const candidates = [countyName, `${countyName} County`];
  for (const candidate of candidates) {
    const countyRes = await supabaseClient
      .from(table)
      .select(`year, ${valueColumn}`)
      .eq('location_name', candidate)
      .eq('sector', 'Commercial')
      .eq('geography_type', 'county')
      .in('year', years);
    if (countyRes.error || !countyRes.data?.length) continue;
    (countyRes.data || []).forEach((r: any) => {
      const y = asNum(r.year);
      const v = asNum(r[valueColumn]);
      if (Number.isFinite(y) && Number.isFinite(v) && v > 0) countySeries.set(y, v);
    });
    if (countySeries.size) break;
  }
  return countySeries;
}

type SoleAnchorCommercialCtx = {
  anchorYear: number;
  anchorKwh: number;
  workerByYear: Map<number, number>;
  countyByYear: Map<number, number>;
  stateElasticityByYear: Map<number, number>;
  years: number[];
};

function soleAnchorM1ThermOrKwh(
  year: number,
  anchorYear: number,
  anchorEnergy: number,
  workerByYear: Map<number, number>
): number {
  const w0 = workerByYear.get(anchorYear);
  const w1 = workerByYear.get(year);
  if (!Number.isFinite(w0) || !Number.isFinite(w1) || (w0 as number) <= 0 || (w1 as number) <= 0) return 0;
  return Math.max(0, anchorEnergy * ((w1 as number) / (w0 as number)));
}

function soleAnchorM3M4FromCounty(
  year: number,
  anchorYear: number,
  anchorEnergy: number,
  countyByYear: Map<number, number>
): number {
  const c0 = countyByYear.get(anchorYear);
  const cy = countyByYear.get(year);
  if (!Number.isFinite(c0) || !Number.isFinite(cy) || (c0 as number) <= 0 || (cy as number) <= 0) return 0;
  return Math.max(0, anchorEnergy * ((cy as number) / (c0 as number)));
}

function soleAnchorM7FromStateElasticity(
  year: number,
  anchorYear: number,
  anchorKwh: number,
  workerByYear: Map<number, number>,
  elasticityByYear: Map<number, number>,
  years: number[]
): number {
  const predicted = new Map<number, number>();
  predicted.set(anchorYear, anchorKwh);
  const ymin = Math.min(...years);
  const ymax = Math.max(...years);
  for (let y = anchorYear + 1; y <= ymax; y++) {
    if (predicted.has(y)) continue;
    const prevYear = y - 1;
    const prevVal = predicted.get(prevYear);
    const d0 = workerByYear.get(prevYear);
    const d1 = workerByYear.get(y);
    const elast = elasticityByYear.get(y);
    if (!prevVal || !d0 || !d1 || !elast) continue;
    const ratio = (d1 as number) / (d0 as number);
    if (!Number.isFinite(ratio) || ratio <= 0) continue;
    predicted.set(y, prevVal * Math.pow(ratio, elast));
  }
  for (let y = anchorYear - 1; y >= ymin; y--) {
    if (predicted.has(y)) continue;
    const nextYear = y + 1;
    const nextVal = predicted.get(nextYear);
    const d0 = workerByYear.get(y);
    const d1 = workerByYear.get(nextYear);
    const elast = elasticityByYear.get(nextYear);
    if (!nextVal || !d0 || !d1 || !elast) continue;
    const ratio = (d1 as number) / (d0 as number);
    if (!Number.isFinite(ratio) || ratio <= 0) continue;
    predicted.set(y, nextVal / Math.pow(ratio, elast));
  }
  const v = predicted.get(year);
  return Number.isFinite(v as number) && (v as number) > 0 ? (v as number) : 0;
}

function soleAnchorModelCandidates(
  year: number,
  ctx: SoleAnchorCommercialCtx
): Record<'m1' | 'm3' | 'm4' | 'm7', number> {
  const m1 = soleAnchorM1ThermOrKwh(year, ctx.anchorYear, ctx.anchorKwh, ctx.workerByYear);
  const m3 = soleAnchorM3M4FromCounty(year, ctx.anchorYear, ctx.anchorKwh, ctx.countyByYear);
  const m4 = m3;
  const m7 = soleAnchorM7FromStateElasticity(
    year,
    ctx.anchorYear,
    ctx.anchorKwh,
    ctx.workerByYear,
    ctx.stateElasticityByYear,
    ctx.years
  );
  return { m1, m3, m4, m7 };
}

/** Exactly one calendar year with positive kWh/therms across observed / interpolated / extrapolated maps (commercial). */
function getSingleCommercialEnergyAnchor(
  observedMap: Map<number, number>,
  interpolatedDirectMap: Map<number, number>,
  extrapolatedDirectMap: Map<number, number>
): { year: number; kwh: number } | null {
  const ys = new Set<number>([
    ...Array.from(observedMap.keys()),
    ...Array.from(interpolatedDirectMap.keys()),
    ...Array.from(extrapolatedDirectMap.keys())
  ]);
  const positiveYears = Array.from(ys)
    .filter((y) => {
      const o = observedMap.get(y) ?? 0;
      const i = interpolatedDirectMap.get(y) ?? 0;
      const e = extrapolatedDirectMap.get(y) ?? 0;
      return o > 0 || i > 0 || e > 0;
    })
    .sort((a, b) => a - b);
  if (positiveYears.length !== 1) return null;
  const y = positiveYears[0];
  const kwh =
    (observedMap.get(y) ?? 0) > 0
      ? (observedMap.get(y) as number)
      : (interpolatedDirectMap.get(y) ?? 0) > 0
        ? (interpolatedDirectMap.get(y) as number)
        : (extrapolatedDirectMap.get(y) as number);
  return { year: y, kwh };
}

function soleAnchorCommercialPick(
  year: number,
  model: CommercialExtrapolationModel,
  ctx: SoleAnchorCommercialCtx
): number {
  const c = soleAnchorModelCandidates(year, ctx);
  switch (model) {
    case 'm1':
      return c.m1;
    case 'm3':
      return c.m3;
    case 'm4':
      return c.m4;
    case 'm7':
      return c.m7;
    case 'm2':
    case 'm5':
    case 'm6':
      return 0;
    case 'average':
    default: {
      // M3 and M4 coincide with a single anchor; average only distinct families (doc: mean user_average × workers + county + state elasticity).
      const vals = [c.m1, c.m3, c.m7].filter((v) => Number.isFinite(v) && v > 0) as number[];
      if (!vals.length) return 0;
      return vals.reduce((s, v) => s + v, 0) / vals.length;
    }
  }
}

let commercialPanelCache: PanelRow[] | null = null;
let commercialPanelCachePromise: Promise<PanelRow[]> | null = null;

/** Resolve elec_* / ng_* format rows: match population geofips + geography_type to DB keys (padded geofips, lowercase geography_type). */
async function fetchEnergyFormatRows(
  table: string,
  selectCols: string,
  sectorLabel: string,
  years: number[],
  geofips: string | number,
  popGeographyType: string | null | undefined
): Promise<{ data: any[]; error: any }> {
  const geoTypes = energyFormatGeographyTypes(popGeographyType);
  const variants = geofipsVariantsForEnergyFormat(geofips);
  let lastError: any = null;
  let firstEmptyOk: any[] | null = null;
  for (const gt of geoTypes) {
    for (const gf of variants) {
      const res = await supabaseClient
        .from(table)
        .select(selectCols)
        .eq('geofips', gf)
        .eq('geography_type', gt)
        .eq('sector', sectorLabel)
        .in('year', years)
        .order('year', { ascending: true });
      if (res.error) {
        lastError = res.error;
        continue;
      }
      lastError = null;
      const rows = res.data ?? [];
      if (rows.length > 0) {
        return { data: rows, error: null };
      }
      if (firstEmptyOk === null) firstEmptyOk = rows;
    }
  }
  return { data: firstEmptyOk ?? [], error: lastError };
}

async function fetchStateEnergyFormatRows(
  table: string,
  selectCols: string,
  sectorLabel: string,
  years: number[]
): Promise<{ data: any[]; error: any }> {
  let lastError: any = null;
  let firstEmptyOk: any[] | null = null;
  for (const gf of stateGeofipsVariantsForEnergyFormat()) {
    const res = await supabaseClient
      .from(table)
      .select(selectCols)
      .eq('geofips', gf)
      .eq('geography_type', 'state')
      .eq('sector', sectorLabel)
      .in('year', years)
      .order('year', { ascending: true });
    if (res.error) {
      lastError = res.error;
      continue;
    }
    lastError = null;
    const rows = res.data ?? [];
    if (rows.length > 0) return { data: rows, error: null };
    if (firstEmptyOk === null) firstEmptyOk = rows;
  }
  return { data: firstEmptyOk ?? [], error: lastError };
}

async function getCommercialPanelData(years: number[]): Promise<PanelRow[]> {
  if (commercialPanelCache) return commercialPanelCache;
  if (commercialPanelCachePromise) return commercialPanelCachePromise;

  commercialPanelCachePromise = (async () => {
    const { data, error } = await supabaseClient
      .from('elec_com_format')
      .select('geofips, year, total_kwh, total_worker, measurement, method_note')
      .in('geography_type', ['city', 'place'])
      .eq('sector', 'Commercial')
      .in('year', years);
    if (error) {
      console.warn('Failed to load commercial panel for M5/M6:', error);
      commercialPanelCache = [];
      return commercialPanelCache;
    }
    commercialPanelCache = (data || [])
      .map((r: any) => ({
        geofips: String(r.geofips ?? ''),
        year: asNum(r.year),
        total_kwh: asNum(r.total_kwh),
        total_worker: asNum(r.total_worker),
        measurement: String(r.measurement || '').toLowerCase().trim(),
        method_note: String(r.method_note || '').toLowerCase().trim()
      }))
      .filter((r) => Number.isFinite(r.year));
    return commercialPanelCache;
  })();

  return commercialPanelCachePromise;
}


function solveLinearSystem(a: number[][], b: number[]): number[] | null {
  const n = a.length;
  const m = a.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let r = col + 1; r < n; r += 1) {
      if (Math.abs(m[r][col]) > Math.abs(m[pivot][col])) pivot = r;
    }
    if (Math.abs(m[pivot][col]) < 1e-12) return null;
    [m[col], m[pivot]] = [m[pivot], m[col]];
    const div = m[col][col];
    for (let c = col; c <= n; c += 1) m[col][c] /= div;
    for (let r = 0; r < n; r += 1) {
      if (r === col) continue;
      const factor = m[r][col];
      for (let c = col; c <= n; c += 1) m[r][c] -= factor * m[col][c];
    }
  }
  return m.map((row) => row[n]);
}

/* eslint-disable no-loop-func */
function fitM5PanelFE(train: PanelRow[]) {
  const rows = train.filter((r) => r.total_kwh > 0 && r.total_worker > 0 && Number.isFinite(r.year));
  if (!rows.length) return null;
  const cities = Array.from(new Set(rows.map((r) => r.geofips)));
  const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
  const ci = new Map(cities.map((c, i) => [c, i]));
  const yi = new Map(years.map((y, i) => [y, i]));
  const alpha = new Array(cities.length).fill(0);
  const gamma = new Array(years.length).fill(0);
  const x = rows.map((r) => Math.log(r.total_worker));
  const y = rows.map((r) => Math.log(r.total_kwh));
  let beta = 0;

  for (let iter = 0; iter < 20; iter += 1) {
    const aSum = new Array(cities.length).fill(0);
    const aCnt = new Array(cities.length).fill(0);
    rows.forEach((r, i) => {
      const c = ci.get(r.geofips)!;
      const yy = yi.get(r.year)!;
      aSum[c] += y[i] - beta * x[i] - gamma[yy];
      aCnt[c] += 1;
    });
    for (let i = 0; i < alpha.length; i += 1) alpha[i] = aCnt[i] ? aSum[i] / aCnt[i] : 0;
    const aMean = mean(alpha.filter((v) => Number.isFinite(v)));
    for (let i = 0; i < alpha.length; i += 1) alpha[i] -= Number.isFinite(aMean) ? aMean : 0;

    const gSum = new Array(years.length).fill(0);
    const gCnt = new Array(years.length).fill(0);
    rows.forEach((r, i) => {
      const c = ci.get(r.geofips)!;
      const yy = yi.get(r.year)!;
      gSum[yy] += y[i] - beta * x[i] - alpha[c];
      gCnt[yy] += 1;
    });
    for (let i = 0; i < gamma.length; i += 1) gamma[i] = gCnt[i] ? gSum[i] / gCnt[i] : 0;
    const gMean = mean(gamma.filter((v) => Number.isFinite(v)));
    for (let i = 0; i < gamma.length; i += 1) gamma[i] -= Number.isFinite(gMean) ? gMean : 0;

    let num = 0;
    let den = 0;
    rows.forEach((r, i) => {
      const c = ci.get(r.geofips)!;
      const yy = yi.get(r.year)!;
      num += x[i] * (y[i] - alpha[c] - gamma[yy]);
      den += x[i] * x[i];
    });
    beta = den > 0 ? num / den : 0;
  }
  return { beta, alphaByCity: ci, alpha, gammaByYear: yi, gamma };
}

function fitM6CityFE(train: PanelRow[]) {
  const rows = train.filter((r) => r.total_kwh > 0 && r.total_worker > 0 && Number.isFinite(r.year));
  if (!rows.length) return null;
  const cities = Array.from(new Set(rows.map((r) => r.geofips)));
  const ci = new Map(cities.map((c, i) => [c, i]));
  const alpha = new Array(cities.length).fill(0);
  let beta = [0, 0, 0, 0]; // intercept, log_worker, year, year2

  for (let iter = 0; iter < 20; iter += 1) {
    const xtx = Array.from({ length: 4 }, () => new Array(4).fill(0));
    const xty = new Array(4).fill(0);
    rows.forEach((r) => {
      const c = ci.get(r.geofips)!;
      const y = Math.log(r.total_kwh) - alpha[c];
      const x = [1, Math.log(r.total_worker), r.year, r.year * r.year];
      for (let i = 0; i < 4; i += 1) {
        xty[i] += x[i] * y;
        for (let j = 0; j < 4; j += 1) xtx[i][j] += x[i] * x[j];
      }
    });
    const solved = solveLinearSystem(xtx, xty);
    if (solved) beta = solved;

    const aSum = new Array(cities.length).fill(0);
    const aCnt = new Array(cities.length).fill(0);
    rows.forEach((r) => {
      const c = ci.get(r.geofips)!;
      const x = [1, Math.log(r.total_worker), r.year, r.year * r.year];
      const yHatNoFE = beta[0] + beta[1] * x[1] + beta[2] * x[2] + beta[3] * x[3];
      aSum[c] += Math.log(r.total_kwh) - yHatNoFE;
      aCnt[c] += 1;
    });
    for (let i = 0; i < alpha.length; i += 1) alpha[i] = aCnt[i] ? aSum[i] / aCnt[i] : 0;
    const aMean = mean(alpha.filter((v) => Number.isFinite(v)));
    for (let i = 0; i < alpha.length; i += 1) alpha[i] -= Number.isFinite(aMean) ? aMean : 0;
  }

  return { beta, alphaByCity: ci, alpha };
}
/* eslint-enable no-loop-func */

function fitLogLinearYear(rows: Array<{ year: number; total_kwh: number }>): { a: number; b: number } | null {
  const valid = rows.filter((r) => Number.isFinite(r.year) && Number.isFinite(r.total_kwh) && r.total_kwh > 0);
  if (valid.length < 2) return null;
  const x = valid.map((r) => r.year);
  const y = valid.map((r) => Math.log(r.total_kwh));
  const xMean = mean(x);
  const yMean = mean(y);
  let num = 0;
  let den = 0;
  for (let i = 0; i < x.length; i += 1) {
    num += (x[i] - xMean) * (y[i] - yMean);
    den += (x[i] - xMean) ** 2;
  }
  if (den <= 0) return null;
  const b = num / den;
  const a = yMean - b * xMean;
  return { a, b };
}

export async function fetchElectricitySeries(
  location: string,
  sector: Sector,
  commercialModel: CommercialExtrapolationModel = 'average'
): Promise<EnergySeriesResult> {
  // Use the new format tables (elec_res_format, elec_com_format)
  const table = sector === 'residential' ? 'elec_res_format' : 'elec_com_format';
  const years = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
  
  try {
    // Get geofips for the location
    const { data: locationData, error: locationError } = await supabaseClient
      .from('population')
      .select('geofips, location_name, geography_type, county')
      .eq('location_name', location)
      .limit(1)
      .single();

    if (locationError || !locationData) {
      console.error('Error fetching location geofips:', locationError);
      throw new Error(`Could not find location: ${location}`);
    }

    // Strip leading zeros for keys that still expect it (e.g. M5/M6 panel merge in legacy path).
    const rawGeofips = String(locationData.geofips).replace(/^0+/, '') || String(locationData.geofips);

    // California State: use ghg_inventory_precalculated activity (county sums) — not elec_*_format.
    if (isCaliforniaStateFromPopulationRow(locationData)) {
      const invRows = await fetchCaliforniaStateEnergyActivityFromGhgInventory(supabaseClient);
      if (invRows.length > 0) {
        const byYear = new Map(invRows.map((r) => [r.year, r]));
        const observedData: number[] = [];
        const interpolatedData: number[] = [];
        const extrapolatedData: number[] = [];
        years.forEach((y) => {
          const row = byYear.get(y);
          const v =
            sector === 'residential'
              ? row?.elec_residential_kwh ?? 0
              : row?.elec_commercial_kwh ?? 0;
          observedData.push(v);
          interpolatedData.push(0);
          extrapolatedData.push(0);
        });
        const sectorLabelElec = sector === 'residential' ? 'Residential' : 'Commercial';
        const suffix = ' (GHG inventory — sum of counties)';
        const yearsWithData = years.filter((y) => {
          const row = byYear.get(y);
          const v =
            sector === 'residential'
              ? row?.elec_residential_kwh ?? 0
              : row?.elec_commercial_kwh ?? 0;
          return v > 0;
        }).length;
        return {
          chartData: {
            labels: years,
            datasets: [
              {
                type: 'bar',
                label: 'Observed',
                data: observedData,
                backgroundColor: sectorColors[sector].observed,
                borderColor: '#374151',
                borderWidth: 1
              },
              {
                type: 'bar',
                label: 'Interpolated',
                data: interpolatedData,
                backgroundColor: sectorColors[sector].interpolated,
                borderColor: '#374151',
                borderWidth: 1
              },
              {
                type: 'bar',
                label: 'Extrapolated',
                data: extrapolatedData,
                backgroundColor: sectorColors[sector].extrapolated,
                borderColor: '#374151',
                borderWidth: 1
              }
            ]
          },
          chartOptions: commonOptions(
            `${sectorLabelElec} Electricity Consumption (kWh) - ${location}${suffix}`,
            'kWh',
            true
          ),
          latestYear: years.length ? years[years.length - 1] : undefined,
          latestValue: observedData.length ? observedData[observedData.length - 1] : undefined,
          availableCommercialModels: sector === 'commercial' ? ['average'] : undefined,
          commercialObservedInterpolatedYears: sector === 'commercial' ? yearsWithData : undefined,
          commercialDepthBucket: sector === 'commercial' ? getCommercialDepthBucket(yearsWithData) : undefined,
          allDataObserved: true
        };
      }
    }

    const denomColumn = sector === 'residential' ? 'population' : 'total_worker';
    const sectorLabel = sector === 'residential' ? 'Residential' : 'Commercial';

    // Fetch data from new format table (fallback across schema variants)
    let energyData: any[] | null = null;
    let energyError: any = null;
    const selectCandidates =
      sector === 'commercial'
        ? [
            // Minimal columns first (always in create_energy_tables.sql); extended model columns may be absent → 400 if selected first.
            `year, total_kwh, ${denomColumn}, measurement, method_note`,
            `year, total_kwh, ${denomColumn}, measurement, method_note, pred_m1, pred_m4, county_total_kwh`,
            `year, total_kwh, ${denomColumn}, measurement, method_note, extrapolated_new, city_obs_interp_years, models_included_in_average, pred_m1, pred_m2, pred_m3, pred_m4, pred_m5, pred_m6, pred_m7, county_total_kwh`,
            `year, total_kwh, ${denomColumn}, measurement, method_note, extrapolated_new, city_obs_interp_years, models_included_in_average, pred_m1, pred_m2, pred_m3, pred_m4, pred_m5, pred_m6, pred_m7, guardrail_m1, guardrail_m2, guardrail_m3, guardrail_m4, guardrail_m5, guardrail_m6, guardrail_m7, county_total_kwh`,
            `year, total_kwh, ${denomColumn}, measurement, method_note, extrapolation_model, extrapolated_new, city_obs_interp_years, models_included_in_average, pred_m1, pred_m2, pred_m3, pred_m4, pred_m5, pred_m6, pred_m7, guardrail_m1, guardrail_m2, guardrail_m3, guardrail_m4, guardrail_m5, guardrail_m6, guardrail_m7, county_total_kwh`
          ]
        : [
            // Base schema (create_energy_tables.sql) has no extrapolation_model — try minimal select first to avoid PostgREST 400 spam.
            `year, total_kwh, ${denomColumn}, measurement, method_note`,
            `year, total_kwh, ${denomColumn}, measurement, method_note, extrapolation_model`
          ];

    for (const selectCols of selectCandidates) {
      const res = await fetchEnergyFormatRows(
        table,
        selectCols,
        sectorLabel,
        years,
        locationData.geofips,
        locationData.geography_type
      );
      if (!res.error) {
        energyData = res.data;
        energyError = null;
        break;
      }
      energyError = res.error;
    }

    if (energyError) {
      console.error('Error fetching energy data:', energyError);
      throw energyError;
    }

    // Fetch statewide data for elasticity-by-year normalization
    const stateRes = await fetchStateEnergyFormatRows(
      table,
      `year, total_kwh, ${denomColumn}, measurement, method_note`,
      sectorLabel,
      years
    );
    const stateEnergyData = stateRes.data;
    if (stateRes.error) {
      console.warn('Error fetching statewide energy data:', stateRes.error);
    }

    const observedMap = new Map<number, number>();
    const interpolatedDirectMap = new Map<number, number>();
    const extrapolatedDirectMap = new Map<number, number>();
    const denomMap = new Map<number, number>();
    const allValueMap = new Map<number, number>();
    const rowByYear = new Map<number, LocalModelRow>();

    (energyData || []).forEach((row: any) => {
      const year = asNum(row.year);
      if (!Number.isFinite(year)) return;
      const value = asNum(row.total_kwh);
      const denomValue = asNum(row[denomColumn]);
      const measurement = String(row.measurement || '').toLowerCase().trim();
      const methodNote = String(row.method_note || '').toLowerCase().trim();
      const extrapolationModel = (row as any).extrapolation_model;
      const provenance = classifyEnergyRowProvenance(measurement, methodNote, extrapolationModel);

      // Require positive kWh for series maps. Rows tagged extrapolated with total_kwh=0 are
      // pipeline placeholders: if we stored them, we'd skip client fallback and show empty years
      // (e.g. SDGE-only 2021–2023 cities like San Diego commercial).
      if (Number.isFinite(value) && value > 0) {
        if (provenance === 'observed' && !observedMap.has(year)) observedMap.set(year, value);
        if (provenance === 'interpolated' && !interpolatedDirectMap.has(year)) interpolatedDirectMap.set(year, value);
        if (provenance === 'extrapolated' && !extrapolatedDirectMap.has(year)) extrapolatedDirectMap.set(year, value);
        if (!allValueMap.has(year)) allValueMap.set(year, value);
      }
      if (Number.isFinite(denomValue) && denomValue > 0 && !denomMap.has(year)) denomMap.set(year, denomValue);

      const existing = rowByYear.get(year);
      const newPriority = provenancePriority(measurement, methodNote, extrapolationModel);
      const existingPriority = existing
        ? provenancePriority(existing.measurement, existing.method_note, existing.extrapolation_model)
        : -1;
      const vPositive = Number.isFinite(value) && value > 0;
      const allowModelOnlyRow = (sector as string) === 'commercial' && commercialEnergyRowHasModelSignal(row);
      if (
        (vPositive || allowModelOnlyRow) &&
        (!existing || newPriority > existingPriority)
      ) {
        rowByYear.set(year, {
          year,
          total_kwh: vPositive ? value : 0,
          total_worker: asNum(row.total_worker),
          county_total_kwh: asNum(row.county_total_kwh),
          extrapolated_new: asNum((row as any).extrapolated_new),
          city_obs_interp_years: asNum((row as any).city_obs_interp_years),
          models_included_in_average: String((row as any).models_included_in_average || ''),
          pred_m1: asNum(row.pred_m1),
          pred_m2: asNum(row.pred_m2),
          pred_m3: asNum((row as any).pred_m3),
          pred_m4: asNum(row.pred_m4),
          pred_m5: asNum((row as any).pred_m5),
          pred_m6: asNum((row as any).pred_m6),
          pred_m7: asNum((row as any).pred_m7),
          guardrail_m1: String((row as any).guardrail_m1 || ''),
          guardrail_m2: String((row as any).guardrail_m2 || ''),
          guardrail_m3: String((row as any).guardrail_m3 || ''),
          guardrail_m4: String((row as any).guardrail_m4 || ''),
          guardrail_m5: String((row as any).guardrail_m5 || ''),
          guardrail_m6: String((row as any).guardrail_m6 || ''),
          guardrail_m7: String((row as any).guardrail_m7 || ''),
          measurement,
          method_note: methodNote,
          extrapolation_model: String(extrapolationModel ?? '')
        });
      }
    });

    if (sector === 'residential') {
      fillInteriorLinearGapsEnergy(years, observedMap, interpolatedDirectMap, extrapolatedDirectMap);
    }

    if ((sector as string) === 'commercial') {
      const observedData: number[] = [];
      const interpolatedData: number[] = [];
      const extrapolatedData: number[] = [];
      const modelPassCounts: Record<Exclude<CommercialExtrapolationModel, 'average'>, number> = {
        m1: 0, m2: 0, m3: 0, m4: 0, m5: 0, m6: 0, m7: 0
      };

      const singleCommercialAnchor = getSingleCommercialEnergyAnchor(
        observedMap,
        interpolatedDirectMap,
        extrapolatedDirectMap
      );
      let soleAnchorCtx: SoleAnchorCommercialCtx | null = null;
      if (singleCommercialAnchor) {
        const anchorYear = singleCommercialAnchor.year;
        const anchorKwh = singleCommercialAnchor.kwh;
        const [lodesMap, countyByYear] = await Promise.all([
          fetchLodesWorkerMap(rawGeofips, locationData.geography_type, years),
          fetchCountyCommercialEnergySeries('elec_com_format', 'total_kwh', locationData.county, years)
        ]);
        const workerByYear = mergeWorkerMapsWithLinearFill(years, denomMap, lodesMap);
        const { stateElasticityByYear } = buildStateElasticityByYear(years, stateEnergyData, 'total_worker');
        soleAnchorCtx = {
          anchorYear,
          anchorKwh,
          workerByYear,
          countyByYear,
          stateElasticityByYear,
          years
        };
      }

      const pickDbCommercialValue = (year: number): number => {
        const row = rowByYear.get(year);
        const candidates: Record<Exclude<CommercialExtrapolationModel, 'average'>, number> = {
          m1: hasGuardrailPass(row, 'm1') ? getDbModelPrediction(row, 'm1') : NaN,
          m2: hasGuardrailPass(row, 'm2') ? getDbModelPrediction(row, 'm2') : NaN,
          m3: hasGuardrailPass(row, 'm3') ? getDbModelPrediction(row, 'm3') : NaN,
          m4: hasGuardrailPass(row, 'm4') ? getDbModelPrediction(row, 'm4') : NaN,
          m5: hasGuardrailPass(row, 'm5') ? getDbModelPrediction(row, 'm5') : NaN,
          m6: hasGuardrailPass(row, 'm6') ? getDbModelPrediction(row, 'm6') : NaN,
          m7: hasGuardrailPass(row, 'm7') ? getDbModelPrediction(row, 'm7') : NaN
        };
        (Object.keys(modelPassCounts) as Array<keyof typeof modelPassCounts>).forEach((k) => {
          if (Number.isFinite(candidates[k]) && (candidates[k] as number) > 0) modelPassCounts[k] += 1;
        });
        if (commercialModel === 'average') {
          const all = Object.values(candidates).filter((v) => Number.isFinite(v) && v > 0) as number[];
          if (all.length) return all.reduce((s, v) => s + v, 0) / all.length;
          const dbAverage = asNum(row?.extrapolated_new);
          if (Number.isFinite(dbAverage) && dbAverage > 0) return dbAverage;
          return 0;
        }
        const selected = candidates[commercialModel];
        return Number.isFinite(selected) && selected > 0 ? selected : 0;
      };

      /**
       * When `elec_com_format` has no row (or no preds) for a year, we still need a series.
       * Interior gaps: linear between anchors. Exterior: linear trend from the first/last *two*
       * training years — not the mean of all training years (that produced a flat line for
       * SDGE-only cities like San Diego with 2021–2023 observed).
       *
       * Single-year anchors (typical NREL 2016, now charted as **Observed**): other years use
       * `soleAnchorCtx` (worker / county / state elasticity). When obs+interp are empty but
       * multiple extrapolated-direct years exist, use those as kWh anchors (linear trend).
       *
       * For a **single** anchor anywhere in obs ∪ interp ∪ extrap, totals scale with
       * `total_worker ×` mean kWh/worker — not a flat kWh repeat (see doc Step 5 commercial).
       */
      const fallbackExtrapolateElec = (year: number): number => {
        const useExtrapAnchorsOnly = observedMap.size === 0 && interpolatedDirectMap.size === 0;
        const knownYears = useExtrapAnchorsOnly
          ? Array.from(extrapolatedDirectMap.keys())
              .filter((y) => (extrapolatedDirectMap.get(y) ?? 0) > 0)
              .sort((a, b) => a - b)
          : Array.from(
              new Set([...Array.from(observedMap.keys()), ...Array.from(interpolatedDirectMap.keys())])
            ).sort((a, b) => a - b);
        if (knownYears.length === 0) return 0;
        if (soleAnchorCtx && knownYears.length === 1) {
          const m1 = soleAnchorM1ThermOrKwh(
            year,
            soleAnchorCtx.anchorYear,
            soleAnchorCtx.anchorKwh,
            soleAnchorCtx.workerByYear
          );
          if (m1 > 0) return m1;
        }
        const valAt = (y: number): number => {
          if (observedMap.has(y)) return observedMap.get(y) as number;
          if (interpolatedDirectMap.has(y)) return interpolatedDirectMap.get(y) as number;
          if (useExtrapAnchorsOnly && extrapolatedDirectMap.has(y)) {
            return extrapolatedDirectMap.get(y) as number;
          }
          return 0;
        };
        const values = knownYears.map(valAt);

        if (year < knownYears[0]) {
          if (knownYears.length >= 2) {
            const y0 = knownYears[0];
            const y1 = knownYears[1];
            const v0 = values[0];
            const v1 = values[1];
            if (y1 !== y0) {
              const slope = (v1 - v0) / (y1 - y0);
              return Math.max(0, v0 + slope * (year - y0));
            }
          }
          return values.reduce((s, v) => s + v, 0) / values.length;
        }
        if (year > knownYears[knownYears.length - 1]) {
          const n = knownYears.length;
          if (n >= 2) {
            const y0 = knownYears[n - 2];
            const y1 = knownYears[n - 1];
            const v0 = values[n - 2];
            const v1 = values[n - 1];
            if (y1 !== y0) {
              const slope = (v1 - v0) / (y1 - y0);
              return Math.max(0, v1 + slope * (year - y1));
            }
          }
          return values.reduce((s, v) => s + v, 0) / values.length;
        }

        const i = knownYears.findIndex((y) => y >= year);
        if (i <= 0) return values[0];
        const y0 = knownYears[i - 1];
        const y1 = knownYears[i];
        const v0 = values[i - 1];
        const v1 = values[i];
        if (y0 === y1) return v0;
        const t = (year - y0) / (y1 - y0);
        return v0 + t * (v1 - v0);
      };

      fillInteriorLinearGapsEnergy(years, observedMap, interpolatedDirectMap, extrapolatedDirectMap);

      years.forEach((year) => {
        if (observedMap.has(year)) {
          observedData.push(observedMap.get(year) as number);
          interpolatedData.push(0);
          extrapolatedData.push(0);
          return;
        }
        if (interpolatedDirectMap.has(year)) {
          observedData.push(0);
          interpolatedData.push(interpolatedDirectMap.get(year) as number);
          extrapolatedData.push(0);
          return;
        }
        const modelPick = pickDbCommercialValue(year);
        if (modelPick > 0) {
          observedData.push(0);
          interpolatedData.push(0);
          extrapolatedData.push(modelPick);
          return;
        }
        if (soleAnchorCtx && modelPick <= 0) {
          const solePick = soleAnchorCommercialPick(year, commercialModel, soleAnchorCtx);
          if (solePick > 0) {
            const cands = soleAnchorModelCandidates(year, soleAnchorCtx);
            (['m1', 'm3', 'm4', 'm7'] as const).forEach((k) => {
              if (Number.isFinite(cands[k]) && cands[k] > 0) modelPassCounts[k] += 1;
            });
            observedData.push(0);
            interpolatedData.push(0);
            extrapolatedData.push(solePick);
            return;
          }
        }
        if (extrapolatedDirectMap.has(year)) {
          const ex = extrapolatedDirectMap.get(year) as number;
          if (Number.isFinite(ex) && ex > 0) {
            observedData.push(0);
            interpolatedData.push(0);
            extrapolatedData.push(ex);
            return;
          }
        }
        const fb = fallbackExtrapolateElec(year);
        if (fb > 0 && isStrictlyInteriorTrainingGap(year, observedMap, interpolatedDirectMap)) {
          observedData.push(0);
          interpolatedData.push(fb);
          extrapolatedData.push(0);
        } else {
          observedData.push(0);
          interpolatedData.push(0);
          extrapolatedData.push(fb);
        }
      });

      const availableCommercialModels: CommercialExtrapolationModel[] = [
        'average',
        ...(Object.entries(modelPassCounts)
          .filter(([, cnt]) => cnt > 0)
          .map(([k]) => k as Exclude<CommercialExtrapolationModel, 'average'>)
        )
      ];
      const depthFromDb = years
        .map((y) => asNum(rowByYear.get(y)?.city_obs_interp_years))
        .find((n) => Number.isFinite(n) && n > 0);
      const commercialObservedInterpolatedYears = Number.isFinite(depthFromDb)
        ? (depthFromDb as number)
        : years.filter((y) => observedMap.has(y) || interpolatedDirectMap.has(y)).length;
      const modelLabel =
        COMMERCIAL_MODEL_OPTIONS.find((o) => o.value === commercialModel)?.label || 'Average (All Models)';

      const hasExtrapolated = extrapolatedData.some((v) => v > 0);
      const chartTitle = hasExtrapolated
        ? `Commercial Electricity Consumption (kWh) - ${location} [Extrapolated: ${modelLabel}]`
        : `Commercial Electricity Consumption (kWh) - ${location}`;
      const extrapolatedElecLabel = hasExtrapolated ? `Extrapolated (${modelLabel})` : 'Extrapolated';
      return {
        chartData: {
          labels: years,
          datasets: [
            { type: 'bar', label: 'Observed', data: observedData, backgroundColor: sectorColors[sector].observed, borderColor: '#374151', borderWidth: 1 },
            { type: 'bar', label: 'Interpolated', data: interpolatedData, backgroundColor: sectorColors[sector].interpolated, borderColor: '#374151', borderWidth: 1 },
            { type: 'bar', label: extrapolatedElecLabel, data: extrapolatedData, backgroundColor: sectorColors[sector].extrapolated, borderColor: '#374151', borderWidth: 1 }
          ]
        },
        chartOptions: commonOptions(chartTitle, 'kWh', true),
        latestYear: years.length ? years[years.length - 1] : undefined,
        latestValue: observedData.length ? (observedData[observedData.length - 1] + interpolatedData[interpolatedData.length - 1] + extrapolatedData[extrapolatedData.length - 1]) : undefined,
        availableCommercialModels,
        commercialObservedInterpolatedYears,
        commercialDepthBucket: getCommercialDepthBucket(commercialObservedInterpolatedYears),
        allDataObserved: !hasExtrapolated
      };
    }

    // Build statewide elasticity by year
    const stateValueMap = new Map<number, number>();
    const stateDenomMap = new Map<number, number>();

    (stateEnergyData || []).forEach((row: any) => {
      const year = row.year;
      const value = parseFloat(row.total_kwh);
      const denomValue = parseFloat(row[denomColumn]);
      if (!isNaN(value) && value > 0) {
        const measurement = (row.measurement || 'observed').toLowerCase().trim();
        const methodNote = (row.method_note || '').toLowerCase().trim();
        const isObserved = measurement === 'observed' && !methodNote.includes('extrapol');

        if (isObserved || !stateValueMap.has(year)) {
          stateValueMap.set(year, value);
        }
      }
      if (!isNaN(denomValue) && denomValue > 0) {
        if (!stateDenomMap.has(year)) {
          stateDenomMap.set(year, denomValue);
        }
      }
    });

    const stateElasticityByYear = new Map<number, number>();
    years.forEach((year) => {
      const prevYear = year - 1;
      const k0 = stateValueMap.get(prevYear);
      const k1 = stateValueMap.get(year);
      const d0 = stateDenomMap.get(prevYear);
      const d1 = stateDenomMap.get(year);
      if (!k0 || !k1 || !d0 || !d1) return;
      if (k0 <= 0 || k1 <= 0 || d0 <= 0 || d1 <= 0) return;
      const logK = Math.log(k1 / k0);
      const logD = Math.log(d1 / d0);
      if (!Number.isFinite(logK) || !Number.isFinite(logD) || logD === 0) return;
      stateElasticityByYear.set(year, logK / logD);
    });

    const observedYears = Array.from(observedMap.keys()).sort((a, b) => a - b);

    let predictedMap = new Map<number, number>(); // M7-style series
    let scale = 1;
    if (sector === 'commercial') {
      // Build predicted series using statewide elasticity by year
      const baseYear = observedYears.length ? observedYears[0] : undefined;
      if (baseYear !== undefined) {
        predictedMap.set(baseYear, observedMap.get(baseYear) as number);
      } else {
        const fallbackYear = years.find((y) => allValueMap.has(y));
        if (fallbackYear !== undefined) {
          predictedMap.set(fallbackYear, allValueMap.get(fallbackYear) as number);
        }
      }

      years.forEach((year) => {
        if (predictedMap.has(year)) return;
        const prevYear = year - 1;
        const prevValue = predictedMap.get(prevYear);
        const d0 = denomMap.get(prevYear);
        const d1 = denomMap.get(year);
        const elasticity = stateElasticityByYear.get(year);
        if (!prevValue || !d0 || !d1 || !elasticity) return;
        const ratio = d1 / d0;
        if (!Number.isFinite(ratio) || ratio <= 0) return;
        predictedMap.set(year, prevValue * Math.pow(ratio, elasticity));
      });

      [...years].reverse().forEach((year) => {
        if (predictedMap.has(year)) return;
        const nextYear = year + 1;
        const nextValue = predictedMap.get(nextYear);
        const d0 = denomMap.get(year);
        const d1 = denomMap.get(nextYear);
        const elasticity = stateElasticityByYear.get(nextYear);
        if (!nextValue || !d0 || !d1 || !elasticity) return;
        const ratio = d1 / d0;
        if (!Number.isFinite(ratio) || ratio <= 0) return;
        predictedMap.set(year, nextValue / Math.pow(ratio, elasticity));
      });

      // Normalize predicted series to observed values without overwriting observed years
      const ratioList: number[] = [];
      observedMap.forEach((observedValue, year) => {
        const predictedValue = predictedMap.get(year);
        if (predictedValue && predictedValue > 0) {
          ratioList.push(observedValue / predictedValue);
        }
      });
      scale =
        ratioList.length > 0
          ? ratioList.sort((a, b) => a - b)[Math.floor(ratioList.length / 2)]
          : 1;
    }

    // M3/M5/M6/M7 maps for commercial model selector
    const m1Map = new Map<number, number>();
    const m2Map = new Map<number, number>();
    const m3Map = new Map<number, number>();
    const m4Map = new Map<number, number>();
    const m5Map = new Map<number, number>();
    const m6Map = new Map<number, number>();
    const m7Map = new Map<number, number>();
    predictedMap.forEach((v, y) => m7Map.set(y, v * scale));

    let cityObservedCount = 0;
    let commercialObservedInterpolatedYears = 0;
    let cityIntMin = NaN;
    let cityIntMax = NaN;
    let globalIntLo = NaN;
    let globalIntHi = NaN;

    if (sector === 'commercial') {
      // Build county totals series (prefer row-level county_total_kwh; fallback to county query).
      const countySeries = new Map<number, number>();
      years.forEach((year) => {
        const c = rowByYear.get(year)?.county_total_kwh;
        if (Number.isFinite(c as number) && (c as number) > 0) countySeries.set(year, c as number);
      });
      if (!countySeries.size && locationData?.county) {
        const countyName = String(locationData.county).replace(/\s*County$/i, '').trim();
        const countyCandidates = [countyName, `${countyName} County`];
        for (const candidate of countyCandidates) {
          const countyRes = await supabaseClient
            .from('elec_com_format')
            .select('year, total_kwh')
            .eq('location_name', candidate)
            .eq('sector', 'Commercial')
            .eq('geography_type', 'county')
            .in('year', years);
          if (!countyRes.error && countyRes.data && countyRes.data.length) {
            (countyRes.data || []).forEach((r: any) => {
              const y = asNum(r.year);
              const v = asNum(r.total_kwh);
              if (Number.isFinite(y) && Number.isFinite(v) && v > 0) countySeries.set(y, v);
            });
            if (countySeries.size) break;
          }
        }
      }

      // M3: county growth propagation anchored at latest training row
      const cityRows = years
        .map((year) => rowByYear.get(year))
        .filter((r): r is LocalModelRow => !!r);
      const trainRows = cityRows.filter((r) => isTrainingRow(r.measurement, r.method_note));
      commercialObservedInterpolatedYears = trainRows.length;

      // Guardrails
      const cityObservedIntensity = cityRows
        .filter(
          (r) =>
            isObservedEnergyRow(r.measurement, r.method_note) &&
            Number.isFinite(r.total_kwh) &&
            r.total_kwh > 0 &&
            Number.isFinite(r.total_worker) &&
            r.total_worker > 0
        )
        .map((r) => r.total_kwh / r.total_worker);
      cityObservedCount = cityObservedIntensity.length;
      if (cityObservedCount) {
        cityIntMin = Math.min(...cityObservedIntensity);
        cityIntMax = Math.max(...cityObservedIntensity);
      }

      // M1: worker intensity
      const m1Rows = trainRows.filter((r) => Number.isFinite(r.total_kwh) && r.total_kwh > 0 && Number.isFinite(r.total_worker) && r.total_worker > 0);
      if (m1Rows.length) {
        const intensity = m1Rows.reduce((s, r) => s + r.total_kwh, 0) / m1Rows.reduce((s, r) => s + r.total_worker, 0);
        years.forEach((year) => {
          const w = rowByYear.get(year)?.total_worker;
          if (Number.isFinite(w as number) && (w as number) > 0) m1Map.set(year, intensity * (w as number));
        });
      }

      // M2: per-city log-linear trend on year
      const m2Fit = fitLogLinearYear(trainRows.map((r) => ({ year: r.year, total_kwh: r.total_kwh })));
      if (m2Fit) {
        years.forEach((year) => {
          m2Map.set(year, Math.exp(m2Fit.a + m2Fit.b * year));
        });
      }

      // M4: ratio-to-county
      const overlap = trainRows.filter((r) => countySeries.has(r.year) && Number.isFinite(r.total_kwh) && r.total_kwh > 0);
      if (overlap.length) {
        const citySum = overlap.reduce((s, r) => s + r.total_kwh, 0);
        const countySum = overlap.reduce((s, r) => s + (countySeries.get(r.year) || 0), 0);
        if (countySum > 0) {
          const ratio = citySum / countySum;
          years.forEach((year) => {
            const c = countySeries.get(year);
            if (Number.isFinite(c as number) && (c as number) > 0) m4Map.set(year, ratio * (c as number));
          });
        }
      }

      const anchorRows = trainRows
        .filter((r) => Number.isFinite(r.total_kwh) && r.total_kwh > 0 && countySeries.has(r.year))
        .sort((a, b) => a.year - b.year);
      if (anchorRows.length) {
        const anchor = anchorRows[anchorRows.length - 1];
        const cAnchor = countySeries.get(anchor.year);
        if (cAnchor && cAnchor > 0) {
        years.forEach((year) => {
            const cYear = countySeries.get(year);
            if (!cYear || cYear <= 0) return;
            m3Map.set(year, anchor.total_kwh * (cYear / cAnchor));
        });
        }
      }

      // M5/M6: fit once on city-year panel training rows.
      const panel = await getCommercialPanelData(years);
      const globalObservedIntensity = panel
        .filter(
          (r) =>
            r.measurement === 'observed' &&
            !r.method_note.includes('extrapol') &&
            Number.isFinite(r.total_kwh) &&
            r.total_kwh > 0 &&
            Number.isFinite(r.total_worker) &&
            r.total_worker > 0
        )
        .map((r) => r.total_kwh / r.total_worker);
      if (globalObservedIntensity.length >= 4) {
        const q1 = percentile(globalObservedIntensity, 0.25);
        const q3 = percentile(globalObservedIntensity, 0.75);
        const iqr = q3 - q1;
        globalIntLo = q1 - 1.5 * iqr;
        globalIntHi = q3 + 1.5 * iqr;
      }
      const panelTrain = panel.filter(
        (r) =>
          isTrainingRow(r.measurement, r.method_note) &&
          Number.isFinite(r.total_kwh) &&
          r.total_kwh > 0 &&
          Number.isFinite(r.total_worker) &&
          r.total_worker > 0
      );
      const m5fit = fitM5PanelFE(panelTrain);
      const m6fit = fitM6CityFE(panelTrain);
      const cityKey = rawGeofips;
      years.forEach((year) => {
        const row = rowByYear.get(year);
        if (!row || !Number.isFinite(row.total_worker) || row.total_worker <= 0) return;
        const lw = Math.log(row.total_worker);
        if (m5fit && m5fit.alphaByCity.has(cityKey) && m5fit.gammaByYear.has(year)) {
          const ai = m5fit.alphaByCity.get(cityKey)!;
          const yi = m5fit.gammaByYear.get(year)!;
          m5Map.set(year, Math.exp(m5fit.alpha[ai] + m5fit.gamma[yi] + m5fit.beta * lw));
        }
        if (m6fit && m6fit.alphaByCity.has(cityKey)) {
          const ai = m6fit.alphaByCity.get(cityKey)!;
          const [b0, b1, b2, b3] = m6fit.beta;
          m6Map.set(year, Math.exp(m6fit.alpha[ai] + b0 + b1 * lw + b2 * year + b3 * year * year));
        }
      });
    }

    // Process data into observed, interpolated, extrapolated arrays
    const observedData: number[] = [];
    const interpolatedData: number[] = [];
    const extrapolatedData: number[] = [];

    const modelPassCounts: Record<Exclude<CommercialExtrapolationModel, 'average'>, number> = {
      m1: 0,
      m2: 0,
      m3: 0,
      m4: 0,
      m5: 0,
      m6: 0,
      m7: 0
    };

    const passesGuardrails = (year: number, pred: number): boolean => {
      const worker = rowByYear.get(year)?.total_worker;
      if (!Number.isFinite(pred) || pred <= 0 || !Number.isFinite(worker as number) || (worker as number) <= 0) {
        return false;
      }
      const intensity = pred / (worker as number);
      // Apply city-specific guardrails only when observed depth is sufficient.
      if (cityObservedCount >= 5) {
        if (Number.isFinite(cityIntMin) && intensity < cityIntMin) return false;
        if (Number.isFinite(cityIntMax) && intensity > cityIntMax) return false;
      }
      if (Number.isFinite(globalIntLo) && intensity < globalIntLo) return false;
      if (Number.isFinite(globalIntHi) && intensity > globalIntHi) return false;
      return true;
    };

      const pickCommercialExtrapolatedValue = (year: number): number => {
      const row = rowByYear.get(year);
      // Prefer precomputed DB model outputs to keep client deterministic and fast.
      const m1 = row?.pred_m1;
      const m2 = row?.pred_m2;
      const m3 = row?.pred_m3;
      const m4 = row?.pred_m4;
      const m5 = row?.pred_m5;
      const m6 = row?.pred_m6;
      const m7 = row?.pred_m7;
      const candidates = {
        m1: passesGuardrails(year, m1 as number) ? (m1 as number) : NaN,
        m2: passesGuardrails(year, m2 as number) ? (m2 as number) : NaN,
        m3: passesGuardrails(year, m3 as number) ? (m3 as number) : NaN,
        m4: passesGuardrails(year, m4 as number) ? (m4 as number) : NaN,
        m5: passesGuardrails(year, m5 as number) ? (m5 as number) : NaN,
        m6: passesGuardrails(year, m6 as number) ? (m6 as number) : NaN,
        m7: passesGuardrails(year, m7 as number) ? (m7 as number) : NaN
      };
      const all = Object.values(candidates).filter((v) => Number.isFinite(v) && v > 0) as number[];

      if (!observedMap.has(year) && !interpolatedDirectMap.has(year)) {
        (Object.keys(modelPassCounts) as Array<keyof typeof modelPassCounts>).forEach((k) => {
          if (Number.isFinite(candidates[k])) modelPassCounts[k] += 1;
        });
      }

      switch (commercialModel) {
        case 'm1':
          return Number.isFinite(candidates.m1) ? candidates.m1 : 0;
        case 'm2':
          return Number.isFinite(candidates.m2) ? candidates.m2 : 0;
        case 'm3':
          return Number.isFinite(candidates.m3) ? candidates.m3 : 0;
        case 'm4':
          return Number.isFinite(candidates.m4) ? candidates.m4 : 0;
        case 'm5':
          return Number.isFinite(candidates.m5) ? candidates.m5 : 0;
        case 'm6':
          return Number.isFinite(candidates.m6) ? candidates.m6 : 0;
        case 'm7':
          return Number.isFinite(candidates.m7) ? candidates.m7 : 0;
        case 'average':
        default:
          return all.length ? all.reduce((s, v) => s + v, 0) / all.length : 0;
      }
    };

    // Build arrays for all years
    years.forEach(year => {
      if (observedMap.has(year)) {
        observedData.push(observedMap.get(year) as number);
        interpolatedData.push(0);
        extrapolatedData.push(0);
        return;
      }

      if (interpolatedDirectMap.has(year)) {
        observedData.push(0);
        interpolatedData.push(interpolatedDirectMap.get(year) as number);
        extrapolatedData.push(0);
        return;
      }

      if (sector === 'residential') {
        if (extrapolatedDirectMap.has(year)) {
          observedData.push(0);
          interpolatedData.push(0);
          extrapolatedData.push(extrapolatedDirectMap.get(year) as number);
          return;
        }
      } else {
        const predictedValue = pickCommercialExtrapolatedValue(year);
        if (predictedValue && Number.isFinite(predictedValue)) {
          observedData.push(0);
          interpolatedData.push(0);
          extrapolatedData.push(predictedValue);
          return;
        }
      }

      observedData.push(0);
      interpolatedData.push(0);
      extrapolatedData.push(0);
    });
    
    const chartData = {
      labels: years,
      datasets: [
        { 
          type: 'bar', 
          label: 'Observed', 
          data: observedData, 
          backgroundColor: sectorColors[sector].observed,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Interpolated', 
          data: interpolatedData, 
          backgroundColor: sectorColors[sector].interpolated,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: sector === 'commercial'
            ? `Extrapolated (${COMMERCIAL_MODEL_OPTIONS.find((o) => o.value === commercialModel)?.label || 'Average'})`
            : 'Extrapolated', 
          data: extrapolatedData, 
          backgroundColor: sectorColors[sector].extrapolated,
          borderColor: '#374151',
          borderWidth: 1
        }
      ]
    };
    
    const latestYear = years.length ? years[years.length - 1] : undefined;
    const latestValue = observedData.length ? 
      (observedData[observedData.length - 1] + interpolatedData[interpolatedData.length - 1] + extrapolatedData[extrapolatedData.length - 1]) : 
      undefined;
    
    const modelLabel =
      sector === 'commercial'
        ? COMMERCIAL_MODEL_OPTIONS.find((o) => o.value === commercialModel)?.label || 'Average (All Models)'
        : null;

    const availableCommercialModels: CommercialExtrapolationModel[] =
      sector === 'commercial'
        ? ([
            'average',
            ...(Object.entries(modelPassCounts)
              .filter(([, cnt]) => cnt > 0)
              .map(([k]) => k as Exclude<CommercialExtrapolationModel, 'average'>))
          ] as CommercialExtrapolationModel[])
        : [];

    const hasExtrapolated = sector === 'commercial' && extrapolatedData.some((v) => v > 0);
    const sectorLabelElec = sector === 'residential' ? 'Residential' : 'Commercial';
    const elecChartTitle = hasExtrapolated && modelLabel
      ? `${sectorLabelElec} Electricity Consumption (kWh) - ${location} [Extrapolated: ${modelLabel}]`
      : `${sectorLabelElec} Electricity Consumption (kWh) - ${location}`;
    return {
      chartData,
      chartOptions: commonOptions(elecChartTitle, 'kWh', true),
      latestYear,
      latestValue,
      availableCommercialModels,
      commercialObservedInterpolatedYears: sector === 'commercial' ? commercialObservedInterpolatedYears : undefined,
      commercialDepthBucket: sector === 'commercial' ? getCommercialDepthBucket(commercialObservedInterpolatedYears) : undefined,
      allDataObserved: sector === 'commercial' ? !hasExtrapolated : undefined
    };
    
  } catch (error) {
    console.error(`Error fetching electricity data for ${location}:`, error);
    throw error;
  }
}

export async function fetchElectricityStacked(
  location: string,
  commercialModel: CommercialExtrapolationModel = 'average'
): Promise<EnergySeriesResult> {
  const [res, com] = await Promise.all([
    fetchElectricitySeries(location, 'residential'),
    fetchElectricitySeries(location, 'commercial', commercialModel)
  ]);
  const labels: number[] = Array.from(new Set([...(res.chartData.labels || []), ...(com.chartData.labels || [])])).sort();

  const resMap = totalByYearFromEnergyChartData(res.chartData);
  const comMap = totalByYearFromEnergyChartData(com.chartData);

  const resSeries = labels.map((y) => (resMap.has(y) ? (resMap.get(y) as number) : 0));
  const comSeries = labels.map((y) => (comMap.has(y) ? (comMap.get(y) as number) : 0));

  return {
    chartData: {
      labels,
      datasets: [
        { type: 'bar', label: 'Residential Electricity', data: resSeries, backgroundColor: sectorColors.residential.observed },
        { type: 'bar', label: 'Commercial Electricity', data: comSeries, backgroundColor: sectorColors.commercial.observed }
      ]
    },
    chartOptions: commonOptions(`Total Electricity Consumption (kWh) - ${location}`, 'kWh', true)
  };
}

export async function fetchNaturalGasSeries(
  location: string,
  sector: Sector,
  commercialModel: CommercialExtrapolationModel = 'average'
): Promise<EnergySeriesResult> {
  // Use the new format tables (ng_res_format, ng_com_format)
  const table = sector === 'residential' ? 'ng_res_format' : 'ng_com_format';
  const years = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
  
  try {
    // Get geofips for the location
    const { data: locationData, error: locationError } = await supabaseClient
      .from('population')
      .select('geofips, location_name, geography_type, county')
      .eq('location_name', location)
      .limit(1)
      .single();

    if (locationError || !locationData) {
      console.error('Error fetching location geofips:', locationError);
      throw new Error(`Could not find location: ${location}`);
    }

    const rawGeofips = String(locationData.geofips).replace(/^0+/, '') || String(locationData.geofips);

    // California State: use ghg_inventory_precalculated activity (county sums) — not ng_*_format.
    if (isCaliforniaStateFromPopulationRow(locationData)) {
      const invRows = await fetchCaliforniaStateEnergyActivityFromGhgInventory(supabaseClient);
      if (invRows.length > 0) {
        const byYear = new Map(invRows.map((r) => [r.year, r]));
        const observedData: number[] = [];
        const interpolatedData: number[] = [];
        const extrapolatedData: number[] = [];
        years.forEach((y) => {
          const row = byYear.get(y);
          const v =
            sector === 'residential'
              ? row?.ng_residential_therms ?? 0
              : row?.ng_commercial_therms ?? 0;
          observedData.push(v);
          interpolatedData.push(0);
          extrapolatedData.push(0);
        });
        const yearsWithData = years.filter((y) => {
          const row = byYear.get(y);
          const v =
            sector === 'residential'
              ? row?.ng_residential_therms ?? 0
              : row?.ng_commercial_therms ?? 0;
          return v > 0;
        }).length;
        const suffix = ' (GHG inventory — sum of counties)';
        if (sector === 'commercial') {
          return {
            chartData: {
              labels: years,
              datasets: [
                {
                  type: 'bar',
                  label: 'Observed',
                  data: observedData,
                  backgroundColor: sectorColors.commercial.observed,
                  borderColor: '#374151',
                  borderWidth: 1
                },
                {
                  type: 'bar',
                  label: 'Interpolated',
                  data: interpolatedData,
                  backgroundColor: sectorColors.commercial.interpolated,
                  borderColor: '#374151',
                  borderWidth: 1
                },
                {
                  type: 'bar',
                  label: 'Extrapolated',
                  data: extrapolatedData,
                  backgroundColor: sectorColors.commercial.extrapolated,
                  borderColor: '#374151',
                  borderWidth: 1
                }
              ]
            },
            chartOptions: commonOptions(
              `Commercial Natural Gas (therms) - ${location}${suffix}`,
              'Therms',
              true
            ),
            latestYear: years.length ? years[years.length - 1] : undefined,
            latestValue: observedData.length ? observedData[observedData.length - 1] : undefined,
            availableCommercialModels: ['average'],
            commercialObservedInterpolatedYears: yearsWithData,
            commercialDepthBucket: getCommercialDepthBucket(yearsWithData),
            allDataObserved: true
          };
        }
        return {
          chartData: {
            labels: years,
            datasets: [
              {
                type: 'bar',
                label: 'Observed',
                data: observedData,
                backgroundColor: sectorColors.residential.observed,
                borderColor: '#374151',
                borderWidth: 1
              },
              {
                type: 'bar',
                label: 'Interpolated',
                data: interpolatedData,
                backgroundColor: sectorColors.residential.interpolated,
                borderColor: '#374151',
                borderWidth: 1
              },
              {
                type: 'bar',
                label: 'Extrapolated',
                data: extrapolatedData,
                backgroundColor: sectorColors.residential.extrapolated,
                borderColor: '#374151',
                borderWidth: 1
              }
            ]
          },
          chartOptions: commonOptions(
            `Residential Natural Gas (therms) - ${location}${suffix}`,
            'Therms',
            true
          ),
          latestYear: years.length ? years[years.length - 1] : undefined,
          latestValue: observedData.length ? observedData[observedData.length - 1] : undefined
        };
      }
    }

    // Fetch data from table with schema fallback variants.
    let energyData: any[] | null = null;
    let energyError: any = null;
    const selectCandidates =
      sector === 'commercial'
        ? [
            'year, total_therms, measurement, method_note, total_worker',
            'year, total_therms, measurement, method_note, total_worker, extrapolated_new, city_obs_interp_years, pred_m1, pred_m2, pred_m3, pred_m4, pred_m5, pred_m6, pred_m7',
            'year, total_therms, measurement, method_note, total_worker, extrapolated_new, city_obs_interp_years, models_included_in_average, pred_m1, pred_m2, pred_m3, pred_m4, pred_m5, pred_m6, pred_m7, guardrail_m1, guardrail_m2, guardrail_m3, guardrail_m4, guardrail_m5, guardrail_m6, guardrail_m7',
            'year, total_therms, measurement, method_note, extrapolation_model, total_worker, extrapolated_new, city_obs_interp_years, models_included_in_average, pred_m1, pred_m2, pred_m3, pred_m4, pred_m5, pred_m6, pred_m7, guardrail_m1, guardrail_m2, guardrail_m3, guardrail_m4, guardrail_m5, guardrail_m6, guardrail_m7'
          ]
        : [
            'year, total_therms, measurement, method_note',
            'year, total_therms, measurement, method_note, extrapolation_model'
          ];
    const sectorLabelGas = sector === 'residential' ? 'Residential' : 'Commercial';
    for (const selectCols of selectCandidates) {
      const res = await fetchEnergyFormatRows(
        table,
        selectCols,
        sectorLabelGas,
        years,
        locationData.geofips,
        locationData.geography_type
      );
      if (!res.error) {
        energyData = res.data || [];
        energyError = null;
        break;
      }
      energyError = res.error;
    }
    if (energyError) {
      console.error('Error fetching energy data:', energyError);
      throw energyError;
    }

    const observedData: number[] = [];
    const interpolatedData: number[] = [];
    const extrapolatedData: number[] = [];

    // Residential: same observed / interpolated / extrapolated + interior gap fill as electricity (cities & counties).
    if (sector === 'residential') {
      const observedMapNg = new Map<number, number>();
      const interpolatedDirectMapNg = new Map<number, number>();
      const extrapolatedDirectMapNg = new Map<number, number>();
      const rowByYearNg = new Map<
        number,
        { therms: number; measurement: string; method_note: string; extrapolation_model: string }
      >();
      (energyData || []).forEach((row: any) => {
        const year = asNum(row.year);
        if (!Number.isFinite(year)) return;
        const therms = asNum(row.total_therms);
        const measurement = String(row.measurement || '').toLowerCase().trim();
        const methodNote = String(row.method_note || '').toLowerCase().trim();
        const extrapolationModel = (row as any).extrapolation_model;
        if (!Number.isFinite(therms) || therms < 0) return;
        const existing = rowByYearNg.get(year);
        const np = provenancePriority(measurement, methodNote, extrapolationModel);
        const ep = existing
          ? provenancePriority(existing.measurement, existing.method_note, existing.extrapolation_model)
          : -1;
        if (!existing || np > ep) {
          rowByYearNg.set(year, {
            therms,
            measurement,
            method_note: methodNote,
            extrapolation_model: String(extrapolationModel ?? '')
          });
        }
      });
      rowByYearNg.forEach((r, y) => {
        const c = classifyEnergyRowProvenance(r.measurement, r.method_note, r.extrapolation_model || undefined);
        if (c === 'observed') observedMapNg.set(y, r.therms);
        else if (c === 'interpolated') interpolatedDirectMapNg.set(y, r.therms);
        else extrapolatedDirectMapNg.set(y, r.therms);
      });
      fillInteriorLinearGapsEnergy(years, observedMapNg, interpolatedDirectMapNg, extrapolatedDirectMapNg);
      years.forEach((year) => {
        observedData.push(0);
        interpolatedData.push(0);
        extrapolatedData.push(0);
        if (observedMapNg.has(year)) observedData[observedData.length - 1] = observedMapNg.get(year) as number;
        else if (interpolatedDirectMapNg.has(year)) interpolatedData[interpolatedData.length - 1] = interpolatedDirectMapNg.get(year) as number;
        else if (extrapolatedDirectMapNg.has(year)) extrapolatedData[extrapolatedData.length - 1] = extrapolatedDirectMapNg.get(year) as number;
      });
    } else {
      // Commercial: same 7-model selector flow as electricity, using therms + workers.
      const rowByYear = new Map<number, LocalModelRow>();
      const observedMap = new Map<number, number>();
      const interpolatedDirectMap = new Map<number, number>();
      const extrapolatedDirectMap = new Map<number, number>();
      const workerMap = new Map<number, number>();

      (energyData || []).forEach((row: any) => {
        const year = asNum(row.year);
        if (!Number.isFinite(year)) return;
        const therms = asNum(row.total_therms);
        const measurement = String(row.measurement || '').toLowerCase().trim();
        const methodNote = String(row.method_note || '').toLowerCase().trim();
        const extrapolationModel = (row as any).extrapolation_model;
        const provenance = classifyEnergyRowProvenance(measurement, methodNote, extrapolationModel);
        if (Number.isFinite(therms) && therms > 0) {
          if (provenance === 'observed' && !observedMap.has(year)) observedMap.set(year, therms);
          if (provenance === 'interpolated' && !interpolatedDirectMap.has(year)) interpolatedDirectMap.set(year, therms);
          if (provenance === 'extrapolated' && !extrapolatedDirectMap.has(year)) extrapolatedDirectMap.set(year, therms);
        }
        const worker = asNum((row as any).total_worker);
        if (Number.isFinite(worker) && worker > 0 && !workerMap.has(year)) workerMap.set(year, worker);
        const existing = rowByYear.get(year);
        const np = provenancePriority(measurement, methodNote, extrapolationModel);
        const ep = existing
          ? provenancePriority(existing.measurement, existing.method_note, existing.extrapolation_model)
          : -1;
        const thermsPositive = Number.isFinite(therms) && therms > 0;
        const allowModelOnlyRow = commercialEnergyRowHasModelSignal(row);
        if (
          (thermsPositive || allowModelOnlyRow) &&
          (!existing || np > ep)
        ) {
          rowByYear.set(year, {
            year,
            total_kwh: thermsPositive ? therms : 0,
            total_worker: worker,
            county_total_kwh: NaN,
            extrapolated_new: asNum((row as any).extrapolated_new),
            city_obs_interp_years: asNum((row as any).city_obs_interp_years),
            models_included_in_average: String((row as any).models_included_in_average || ''),
            pred_m1: asNum((row as any).pred_m1),
            pred_m2: asNum((row as any).pred_m2),
            pred_m3: asNum((row as any).pred_m3),
            pred_m4: asNum((row as any).pred_m4),
            pred_m5: asNum((row as any).pred_m5),
            pred_m6: asNum((row as any).pred_m6),
            pred_m7: asNum((row as any).pred_m7),
            guardrail_m1: String((row as any).guardrail_m1 || ''),
            guardrail_m2: String((row as any).guardrail_m2 || ''),
            guardrail_m3: String((row as any).guardrail_m3 || ''),
            guardrail_m4: String((row as any).guardrail_m4 || ''),
            guardrail_m5: String((row as any).guardrail_m5 || ''),
            guardrail_m6: String((row as any).guardrail_m6 || ''),
            guardrail_m7: String((row as any).guardrail_m7 || ''),
            measurement,
            method_note: methodNote,
            extrapolation_model: String(extrapolationModel ?? '')
          });
        }
      });

      const singleCommercialAnchorNg = getSingleCommercialEnergyAnchor(
        observedMap,
        interpolatedDirectMap,
        extrapolatedDirectMap
      );
      let soleAnchorCtxNg: SoleAnchorCommercialCtx | null = null;
      if (singleCommercialAnchorNg) {
        const anchorYear = singleCommercialAnchorNg.year;
        const anchorKwh = singleCommercialAnchorNg.kwh;
        const stateResNg = await fetchStateEnergyFormatRows(
          'ng_com_format',
          'year, total_therms, total_worker, measurement, method_note',
          'Commercial',
          years
        );
        const [lodesMapNg, countyTherms] = await Promise.all([
          fetchLodesWorkerMap(rawGeofips, locationData.geography_type, years),
          fetchCountyCommercialEnergySeries('ng_com_format', 'total_therms', locationData.county, years)
        ]);
        const workerByYearNg = mergeWorkerMapsWithLinearFill(years, workerMap, lodesMapNg);
        const { stateElasticityByYear: stateElastNg } = buildStateElasticityByYear(
          years,
          stateResNg.data,
          'total_worker'
        );
        soleAnchorCtxNg = {
          anchorYear,
          anchorKwh,
          workerByYear: workerByYearNg,
          countyByYear: countyTherms,
          stateElasticityByYear: stateElastNg,
          years
        };
      }

      // Fast path: use DB-precomputed model outputs + guardrails directly.
      {
      const modelPassCounts: Record<Exclude<CommercialExtrapolationModel, 'average'>, number> = {
        m1: 0, m2: 0, m3: 0, m4: 0, m5: 0, m6: 0, m7: 0
      };
      const pickDbCommercialValue = (year: number): number => {
        const row = rowByYear.get(year);
        const candidates: Record<Exclude<CommercialExtrapolationModel, 'average'>, number> = {
          m1: hasGuardrailPass(row, 'm1') ? getDbModelPrediction(row, 'm1') : NaN,
          m2: hasGuardrailPass(row, 'm2') ? getDbModelPrediction(row, 'm2') : NaN,
          m3: hasGuardrailPass(row, 'm3') ? getDbModelPrediction(row, 'm3') : NaN,
          m4: hasGuardrailPass(row, 'm4') ? getDbModelPrediction(row, 'm4') : NaN,
          m5: hasGuardrailPass(row, 'm5') ? getDbModelPrediction(row, 'm5') : NaN,
          m6: hasGuardrailPass(row, 'm6') ? getDbModelPrediction(row, 'm6') : NaN,
          m7: hasGuardrailPass(row, 'm7') ? getDbModelPrediction(row, 'm7') : NaN
        };
        (Object.keys(modelPassCounts) as Array<keyof typeof modelPassCounts>).forEach((k) => {
          if (Number.isFinite(candidates[k]) && (candidates[k] as number) > 0) modelPassCounts[k] += 1;
        });
        if (commercialModel === 'average') {
          const all = Object.values(candidates).filter((v) => Number.isFinite(v) && v > 0) as number[];
          if (all.length) return all.reduce((s, v) => s + v, 0) / all.length;
          const dbAverage = asNum(row?.extrapolated_new);
          if (Number.isFinite(dbAverage) && dbAverage > 0) return dbAverage;
          return 0;
        }
        const selected = candidates[commercialModel];
        return Number.isFinite(selected) && selected > 0 ? selected : 0;
      };

      // Same exterior logic as commercial electricity (trend from two anchors, not flat mean).
      // NREL / modeled-only anchors live in extrapolatedDirectMap when strict obs is empty — see fallbackExtrapolateElec.
      const fallbackExtrapolateGas = (year: number): number => {
        const useExtrapAnchorsOnly = observedMap.size === 0 && interpolatedDirectMap.size === 0;
        const knownYears = useExtrapAnchorsOnly
          ? Array.from(extrapolatedDirectMap.keys())
              .filter((y) => (extrapolatedDirectMap.get(y) ?? 0) > 0)
              .sort((a, b) => a - b)
          : Array.from(
              new Set([...Array.from(observedMap.keys()), ...Array.from(interpolatedDirectMap.keys())])
            ).sort((a, b) => a - b);
        if (knownYears.length === 0) return 0;
        if (soleAnchorCtxNg && knownYears.length === 1) {
          const m1g = soleAnchorM1ThermOrKwh(
            year,
            soleAnchorCtxNg.anchorYear,
            soleAnchorCtxNg.anchorKwh,
            soleAnchorCtxNg.workerByYear
          );
          if (m1g > 0) return m1g;
        }
        const valAt = (y: number): number => {
          if (observedMap.has(y)) return observedMap.get(y) as number;
          if (interpolatedDirectMap.has(y)) return interpolatedDirectMap.get(y) as number;
          if (useExtrapAnchorsOnly && extrapolatedDirectMap.has(y)) {
            return extrapolatedDirectMap.get(y) as number;
          }
          return 0;
        };
        const values = knownYears.map(valAt);

        if (year < knownYears[0]) {
          if (knownYears.length >= 2) {
            const y0 = knownYears[0];
            const y1 = knownYears[1];
            const v0 = values[0];
            const v1 = values[1];
            if (y1 !== y0) {
              const slope = (v1 - v0) / (y1 - y0);
              return Math.max(0, v0 + slope * (year - y0));
            }
          }
          return values.reduce((s, v) => s + v, 0) / values.length;
        }
        if (year > knownYears[knownYears.length - 1]) {
          const n = knownYears.length;
          if (n >= 2) {
            const y0 = knownYears[n - 2];
            const y1 = knownYears[n - 1];
            const v0 = values[n - 2];
            const v1 = values[n - 1];
            if (y1 !== y0) {
              const slope = (v1 - v0) / (y1 - y0);
              return Math.max(0, v1 + slope * (year - y1));
            }
          }
          return values.reduce((s, v) => s + v, 0) / values.length;
        }

        const i = knownYears.findIndex((y) => y >= year);
        if (i <= 0) return values[0];
        const y0 = knownYears[i - 1];
        const y1 = knownYears[i];
        const v0 = values[i - 1];
        const v1 = values[i];
        if (y0 === y1) return v0;
        const t = (year - y0) / (y1 - y0);
        return v0 + t * (v1 - v0);
      };

      fillInteriorLinearGapsEnergy(years, observedMap, interpolatedDirectMap, extrapolatedDirectMap);

      years.forEach((y) => {
        observedData.push(0);
        interpolatedData.push(0);
        extrapolatedData.push(0);
        const idx = observedData.length - 1;
        if (observedMap.has(y)) {
          observedData[idx] = observedMap.get(y) as number;
          return;
        }
        if (interpolatedDirectMap.has(y)) {
          interpolatedData[idx] = interpolatedDirectMap.get(y) as number;
          return;
        }
        const modelPick = pickDbCommercialValue(y);
        if (modelPick > 0) {
          extrapolatedData[idx] = modelPick;
          return;
        }
        if (soleAnchorCtxNg && modelPick <= 0) {
          const solePickNg = soleAnchorCommercialPick(y, commercialModel, soleAnchorCtxNg);
          if (solePickNg > 0) {
            const cng = soleAnchorModelCandidates(y, soleAnchorCtxNg);
            (['m1', 'm3', 'm4', 'm7'] as const).forEach((k) => {
              if (Number.isFinite(cng[k]) && cng[k] > 0) modelPassCounts[k] += 1;
            });
            extrapolatedData[idx] = solePickNg;
            return;
          }
        }
        if (extrapolatedDirectMap.has(y)) {
          extrapolatedData[idx] = extrapolatedDirectMap.get(y) as number;
          return;
        }
        const fb = fallbackExtrapolateGas(y);
        if (fb > 0 && isStrictlyInteriorTrainingGap(y, observedMap, interpolatedDirectMap)) {
          interpolatedData[idx] = fb;
        } else {
          extrapolatedData[idx] = fb;
        }
      });

      const available = ([
        'average',
        ...(Object.entries(modelPassCounts).filter(([, c]) => c > 0).map(([k]) => k))
      ] as CommercialExtrapolationModel[]);
      const depthFromDb = years
        .map((y) => asNum(rowByYear.get(y)?.city_obs_interp_years))
        .find((n) => Number.isFinite(n) && n > 0);
      const dbCommercialObservedInterpolatedYears = Number.isFinite(depthFromDb)
        ? (depthFromDb as number)
        : years.filter((y) => observedMap.has(y) || interpolatedDirectMap.has(y)).length;
      const modelLabel = COMMERCIAL_MODEL_OPTIONS.find((o) => o.value === commercialModel)?.label || 'Average (All Models)';
      const hasExtrapolatedGas = extrapolatedData.some((v) => v > 0);
      const extrapolatedGasLabel = hasExtrapolatedGas ? `Extrapolated (${modelLabel})` : 'Extrapolated';
      const chartDataGas = {
        labels: years,
        datasets: [
          { type: 'bar', label: 'Observed', data: observedData, backgroundColor: sectorColors[sector].observed, borderColor: '#374151', borderWidth: 1 },
          { type: 'bar', label: 'Interpolated', data: interpolatedData, backgroundColor: sectorColors[sector].interpolated, borderColor: '#374151', borderWidth: 1 },
          { type: 'bar', label: extrapolatedGasLabel, data: extrapolatedData, backgroundColor: sectorColors[sector].extrapolated, borderColor: '#374151', borderWidth: 1 }
        ]
      };
      const latestYear = years.length ? years[years.length - 1] : undefined;
      const latestValue = observedData.length
        ? (observedData[observedData.length - 1] || interpolatedData[interpolatedData.length - 1] || extrapolatedData[extrapolatedData.length - 1] || 0)
        : undefined;
      const gasChartTitle = hasExtrapolatedGas
        ? `Commercial Natural Gas (therms) - ${location} [Extrapolated: ${modelLabel}]`
        : `Commercial Natural Gas (therms) - ${location}`;
      return {
        chartData: chartDataGas,
        chartOptions: commonOptions(gasChartTitle, 'Therms', true),
        latestYear,
        latestValue,
        availableCommercialModels: available,
        commercialObservedInterpolatedYears: dbCommercialObservedInterpolatedYears,
        commercialDepthBucket: getCommercialDepthBucket(dbCommercialObservedInterpolatedYears),
        allDataObserved: !hasExtrapolatedGas
      };
      }

    }
    
    const chartDataGas = {
      labels: years,
      datasets: [
        { 
          type: 'bar', 
          label: 'Observed', 
          data: observedData, 
          backgroundColor: sectorColors[sector].observed,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Interpolated', 
          data: interpolatedData, 
          backgroundColor: sectorColors[sector].interpolated,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Extrapolated', 
          data: extrapolatedData, 
          backgroundColor: sectorColors[sector].extrapolated,
          borderColor: '#374151',
          borderWidth: 1
        }
      ]
    };
    
    const latestYear = years.length ? years[years.length - 1] : undefined;
    const latestValue = observedData.length ? 
      (observedData[observedData.length - 1] || interpolatedData[interpolatedData.length - 1] || extrapolatedData[extrapolatedData.length - 1] || 0) : 
      undefined;
    
    // This path is residential only (commercial returns earlier)
    return {
      chartData: chartDataGas,
      chartOptions: commonOptions('Residential Natural Gas (therms)', 'Therms', true),
      latestYear,
      latestValue
    };
    
  } catch (error) {
    console.error(`Error fetching natural gas data for ${location}:`, error);
    throw error;
  }
}

export async function fetchNaturalGasStacked(
  location: string,
  commercialModel: CommercialExtrapolationModel = 'average'
): Promise<EnergySeriesResult> {
  const [res, com] = await Promise.all([
    fetchNaturalGasSeries(location, 'residential'),
    fetchNaturalGasSeries(location, 'commercial', commercialModel)
  ]);
  const labels: number[] = Array.from(new Set([...(res.chartData.labels || []), ...(com.chartData.labels || [])])).sort();

  const resMap = totalByYearFromEnergyChartData(res.chartData);
  const comMap = totalByYearFromEnergyChartData(com.chartData);

  const resSeries = labels.map((y) => (resMap.has(y) ? (resMap.get(y) as number) : 0));
  const comSeries = labels.map((y) => (comMap.has(y) ? (comMap.get(y) as number) : 0));

  return {
    chartData: {
      labels,
      datasets: [
        { type: 'bar', label: 'Residential Natural Gas', data: resSeries, backgroundColor: sectorColors.residential.observed },
        { type: 'bar', label: 'Commercial Natural Gas', data: comSeries, backgroundColor: sectorColors.commercial.observed }
      ]
    },
    chartOptions: commonOptions(`Total Natural Gas (therms) - ${location}`, 'Therms', true)
  };
}


